import {
  Text,
  Container,
  Space,
  Title,
  Group,
  Card,
  Tabs,
  SimpleGrid,
  Box,
  Badge,
  Table,
  Button,
  Center
} from "@mantine/core";
import { IconCrown } from "@tabler/icons-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { MantineReactTable, MRT_ColumnDef } from "mantine-react-table";
import { Link, useNavigate } from "react-router-dom";
import { ProfileInformation } from "./ProfileInformation";
import { PricingService } from "../../services/pricingService";
import { ConfigContext } from '../../Context/configContext';
import { useTranslation } from 'react-i18next';

interface SubscriptionRow {
  category: string;
  price: number;
  currency: string;
  startedOn: string;
  timeline: string;
  status: string;
}

export function BillingSubscriptionPage(props: any): JSX.Element {
  const { t } = useTranslation();
  const router = useNavigate();
  const { config } = useContext(ConfigContext);
  const pricingService = useMemo(() => new PricingService(config!), [config]);
  const [data, setData] = useState<SubscriptionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [domainId, setDomainId] = useState<string | null>(props?.currentConsentData?.domainId || localStorage.getItem('activeDomainId'));

  useEffect(() => {
    if (props.currentConsentData) {
      setDomainId(props.currentConsentData.domainId);
    }
  }, [props.currentConsentData]);

  const fetchSubscriptions = async (domainId: string) => {
    setLoading(true);
    try {
      const response = await pricingService.getSubscriptions(domainId);
      setData(response?.data);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString();
  };

  const calculateExpiry = (startDate: string, timeline: string): string => {
    if (!startDate || !timeline) return "";
    const start = new Date(startDate);
    const now = new Date();
    
    if (timeline === "monthly") {
      start.setMonth(start.getMonth() + 1);
    } else if (timeline === "yearly") {
      start.setFullYear(start.getFullYear() + 1);
    }

    return start.toLocaleDateString();
  };

  const columns = useMemo<MRT_ColumnDef<SubscriptionRow>[]>(() => [
    {
      accessorKey: "category",
      header: t('billing.category')
    },
    {
      accessorKey: "price",
      header: t('billing.price'),
      Cell: ({ row }) => {
        const { price, currency } = row.original;
        return (
          <Text size="sm" fw={700}>
            {currency === "INR" ? `₹${price}` : `$${price}`}
          </Text>
        );
      },
    },
    {
      accessorKey: "startedOn",
      header: t('billing.startedOn'),
      Cell: ({ row }) => (
        <Text size="sm">{formatDate(row.original.startedOn)}</Text>
      ),
    },
    {
      accessorKey: "expiresOn",
      header: t('billing.expiresOn'),
      Cell: ({ row }) => {
        const expiry = calculateExpiry(row.original.startedOn, row.original.timeline);
        return (
          <Text size="sm">{expiry || "N/A"}</Text>
        );
      },
    },
    {
      accessorKey: "status",
      header: t('billing.status'),
      Cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge color={status === "active" ? "green" : status === "expired" ? "red" : "orange"}>
            {status === 'active' ? t('billing.active') : status === 'expired' ? t('billing.expired') : t('billing.pending')}
          </Badge>
        );
      },
    },
    {
      header: t('billing.action'),
      Cell: ({ row }) => (
        <Link to={`/pricing/?domainId=${domainId}`}>
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconCrown />}
          >
            {t('billing.upgrade')}
          </Button>
        </Link>
      ),
    },
  ], [domainId]);

  useEffect(() => {
    if (domainId) {
      fetchSubscriptions(domainId);
    }
  }, [domainId]);

  return (
    <Container fluid>
      <Space h="lg" />
      <Title order={2}>{t('billing.title')}</Title>
      <Space h="lg" />
      <Card withBorder shadow="sm" radius="md">
        <Card.Section withBorder inheritPadding mt="sm" pb="md">
          <Tabs defaultValue="account">
            <Tabs.List>
              <Tabs.Tab value="account">{t('billing.account')}</Tabs.Tab>
              <Tabs.Tab value="BillingInformation">{t('billing.billingInformation')}</Tabs.Tab>
              <Tabs.Tab value="subscription">{t('billing.subscription')}</Tabs.Tab>
              <Tabs.Tab value="invoices">{t('billing.invoices')}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="account">
              <ProfileInformation isBilling={false} />
            </Tabs.Panel>

            <Tabs.Panel value="BillingInformation">
              <ProfileInformation isBilling={true} />
            </Tabs.Panel>

            <Tabs.Panel value="subscription">
              <Card shadow="0">
                <Card.Section withBorder inheritPadding py="xs">
                  <SimpleGrid cols={1} mt={10} mb={20}>
                    <Box>
                      {data.length > 0 ? (
                        <MantineReactTable
                          columns={columns}
                          data={data}
                          state={{ isLoading: loading }}
                          enablePagination
                          enableColumnActions={false}
                          initialState={{
                            showGlobalFilter: true,
                          }}
                          mantineSearchTextInputProps={{
                            placeholder: t('billing.searchSubscriptions'),
                            style: { minWidth: "28rem", display: "block", marginLeft: 'auto' },
                            variant: "filled",
                          }}
                          mantineTableHeadCellProps={{
                            style: {
                              backgroundColor: "#f9f9f9",
                              color: "#000",
                              fontWeight: 600,
                            },
                          }}
                          mantineTableBodyCellProps={{
                            style: {
                              padding: "12px",
                              borderBottom: "1px solid #eee",
                              transition: "background-color 0.3s",
                            },
                          }}
                        />
                      ) : (
                        <Center><Text>{t('billing.noDataAvailable')}</Text></Center>
                      )}
                    </Box>
                  </SimpleGrid>
                </Card.Section>
              </Card>
            </Tabs.Panel>

            <Tabs.Panel value="invoices">
              <Card shadow="0">
                <Card.Section withBorder inheritPadding py="xs">
                  <Group>
                    <Text size="sm" mt={10} fw={600}>
                      {t('billing.invoices')}
                    </Text>
                  </Group>
                  <SimpleGrid cols={1} mt={10} mb={20}>
                    <Table mt={20}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t('billing.invoiceNumber')}</Table.Th>
                          <Table.Th>{t('billing.createdDate')}</Table.Th>
                          <Table.Th>{t('billing.action')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {/* Add invoices data here */}
                      </Table.Tbody>
                    </Table>
                  </SimpleGrid>
                </Card.Section>
              </Card>
            </Tabs.Panel>
          </Tabs>
        </Card.Section>
      </Card>
    </Container>
  );
}